import React from 'react';
import Icon from '../icons/Icon';
import Sparkline from './Sparkline';

export type MetricTone = 'neutral' | 'positive' | 'negative' | 'caution';

export interface MetricDelta {
  /** Percentage change against the comparison period, e.g. -4.2 for -4.2%. */
  percent: number;
  /** What it is being compared against, e.g. "vs previous 30 days". */
  comparedTo: string;
  /**
   * Whether an increase is good. Refunds and error counts set this false so
   * the colour and the arrow disagree in the way the reader expects.
   */
  higherIsBetter?: boolean;
}

export interface MetricTarget {
  label: string;
  /** 0-100. Values outside the range are clamped. */
  percent: number;
}

interface MetricCardProps {
  label: string;
  value: React.ReactNode;
  /** Secondary line: the supporting count, denominator, or qualifier. */
  hint?: string;
  /**
   * Period-over-period context. Deliberately optional — several endpoints in
   * this console return a single all-time total with nothing to compare it
   * against, and inventing a delta for those would be worse than omitting one.
   */
  delta?: MetricDelta;
  /** Progress toward a quota or cap, drawn as a slim meter under the value. */
  target?: MetricTarget;
  /** Oldest value first. Rendered as a sparkline beside the number. */
  trend?: number[];
  tone?: MetricTone;
  /** Turns the whole card into a button for drill-down. */
  onClick?: () => void;
  isLoading?: boolean;
  className?: string;
}

const TONE_TEXT: Record<MetricTone, string> = {
  neutral: 'text-ink',
  positive: 'text-positive',
  negative: 'text-negative',
  caution: 'text-caution',
};

function formatPercent(percent: number): string {
  const rounded = Math.abs(percent) >= 10 ? Math.round(percent) : Math.round(percent * 10) / 10;
  return `${rounded > 0 ? '+' : ''}${rounded}%`;
}

const MetricCard: React.FC<MetricCardProps> = ({
  label,
  value,
  hint,
  delta,
  target,
  trend,
  tone = 'neutral',
  onClick,
  isLoading = false,
  className = '',
}) => {
  if (isLoading) {
    return (
      <div
        className={`rounded-card border border-border-subtle bg-surface p-5 shadow-card ${className}`}
        aria-busy="true"
      >
        <div className="animate-shimmer space-y-3">
          <div className="h-3.5 w-24 rounded bg-surface-active" />
          <div className="h-8 w-20 rounded bg-surface-active" />
          <div className="h-3 w-28 rounded bg-surface-active" />
        </div>
        <span className="sr-only">Loading {label}</span>
      </div>
    );
  }

  const improving = delta
    ? delta.percent === 0
      ? null
      : delta.percent > 0 === (delta.higherIsBetter ?? true)
    : null;

  const deltaTone =
    improving === null ? 'text-ink-3 bg-surface-sunken' : improving ? 'text-positive bg-positive-bg' : 'text-negative bg-negative-bg';

  const body = (
    <>
      <div className="flex items-start justify-between gap-3">
        <h3 className="text-sm font-semibold leading-5 text-ink-2">{label}</h3>
        {trend && trend.length > 1 ? (
          <span className={improving === false ? 'text-negative' : 'text-brand-blue-light'}>
            <Sparkline points={trend} />
          </span>
        ) : null}
      </div>

      <div className="mt-2 flex items-baseline gap-2">
        <span className={`text-3xl font-bold leading-none tracking-tight ${TONE_TEXT[tone]}`}>{value}</span>
      </div>

      <div className="mt-2.5 flex flex-wrap items-center gap-x-2 gap-y-1">
        {delta ? (
          <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${deltaTone}`}>
            <Icon
              name={delta.percent === 0 ? 'dash' : delta.percent > 0 ? 'arrowUp' : 'arrowDown'}
              className="h-3 w-3"
              strokeWidth={2.5}
            />
            {formatPercent(delta.percent)}
          </span>
        ) : null}
        {delta ? <span className="text-xs text-ink-3">{delta.comparedTo}</span> : null}
        {!delta && hint ? <span className="text-xs text-ink-3">{hint}</span> : null}
      </div>

      {delta && hint ? <p className="mt-1 text-xs text-ink-3">{hint}</p> : null}

      {target ? (
        <div className="mt-3">
          <div className="flex items-center justify-between text-xs text-ink-3">
            <span>{target.label}</span>
            <span className="font-semibold text-ink-2">{Math.round(Math.min(Math.max(target.percent, 0), 100))}%</span>
          </div>
          <div
            className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-surface-active"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(Math.min(Math.max(target.percent, 0), 100))}
            aria-label={target.label}
          >
            <div
              className={`h-full rounded-full ${target.percent >= 90 ? 'bg-caution' : 'bg-brand-blue'}`}
              style={{ width: `${Math.min(Math.max(target.percent, 0), 100)}%` }}
            />
          </div>
        </div>
      ) : null}
    </>
  );

  const shell = 'rounded-card border border-border-subtle bg-surface p-5 shadow-card transition-shadow';

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={`${shell} w-full text-left hover:border-border-default hover:shadow-card-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue focus-visible:ring-offset-2 ${className}`}
      >
        {body}
      </button>
    );
  }

  return <div className={`${shell} ${className}`}>{body}</div>;
};

export default MetricCard;
